import type { h as hFunction, VNode } from 'vue';
import { createTextVNode } from 'vue';
import type { TContent } from '~/types/common';

// без асинхронного импорта, потому что стили из этого компонента отображаются
// на первом экране десктопа и "прыгают" во время загрузки
import TopPosts from '~/components/BestArticlesWidget.vue';

const PostsCollection = defineAsyncComponent(() => import('~/components/PostsCollection.vue'));
const TaxonomyPosts = defineAsyncComponent(() => import('~/components/TaxonomyPosts.vue'));

const TheTable = defineAsyncComponent(() => import('~/components/Table.vue'));
const TheHeading = defineAsyncComponent(() => import('~/components/TheHeading.vue'));
const Infobox = defineAsyncComponent(() => import('~/components/BaseComponents/BaseInfoBox.vue'));
const Incut = defineAsyncComponent(() => import('~/components/BaseComponents/BaseIncut.vue'));
const CommentsWidget = defineAsyncComponent(() => import('~/components/CommentsWidget.vue'));
const CoreButtons = defineAsyncComponent(() => import('~/components/CoreButtons.vue'));
const ArticleButton = defineAsyncComponent(
  () => import('~/components/BaseComponents/ArticleButton.vue'),
);
const GreyBox = defineAsyncComponent(() => import('~/components/GreyBox.vue'));
const SubscriptionsForm = defineAsyncComponent(
  () => import('~/components/NewsSubscriptionsForm/NewsSubscriptionsForm.vue'),
);
const BHLink = defineAsyncComponent(() => import('~/components/BHLink.vue'));
const TheParagraph = defineAsyncComponent(() => import('~/components/TheParagraph.vue'));
const Gallery = defineAsyncComponent(() => import('~/components/Gallery/BaseGallery.vue'));
const Quote = defineAsyncComponent(() => import('~/components/BaseComponents/BaseQuote.vue'));
const ArticlePicture = defineAsyncComponent(() => import('~/components/ArticlePicture.vue'));
const AmpArticlePicture = defineAsyncComponent(
  () => import('~/components/Amp/AmpArticlePicture.amp.vue'),
);
const AmpMobileScreenShots = defineAsyncComponent(
  () => import('~/components/Amp/AmpMobileScreenshots.amp.vue'),
);
const AmpGallery = defineAsyncComponent(() => import('~/components/Amp/AmpGallery.amp.vue'));
const AmpGalleryProducts = defineAsyncComponent(
  () => import('~/components/Amp/GalleryProducts.amp.vue'),
);
const BaseSeparator = defineAsyncComponent(
  () => import('~/components/BaseComponents/BaseSeparator.vue'),
);
const BHList = defineAsyncComponent(() => import('~/components/BHList.vue'));
const CharactersList = defineAsyncComponent(() => import('~/components/CharactersList.vue'));
const BHGroup = defineAsyncComponent(() => import('~/components/BHGroup.vue'));
const MobileScreenshots = defineAsyncComponent(() => import('~/components/MobileScreenshots.vue'));
const AppHtml = defineAsyncComponent(() => import('~/components/AppHtml.vue'));
const CoreColumns = defineAsyncComponent(() => import('~/components/CoreColumns.vue'));
const CoreColumn = defineAsyncComponent(() => import('~/components/CoreColumn.vue'));
const CoreTableOfContents = defineAsyncComponent(
  () => import('~/components/CoreTableOfContents.vue'),
);
const PinterestColumns = defineAsyncComponent(() => import('~/components/PinterestColumns.vue'));
const QuickPolls = defineAsyncComponent(
  () => import('~/components/BaseComponents/BaseQuickPoll/BaseQuickPoll.vue'),
);
const BasePromoblok = defineAsyncComponent(
  () => import('~/components/BaseComponents/BasePromoblock.vue'),
);

const Spoiler = defineAsyncComponent(() => import('~/components/BaseComponents/BaseSpoiler.vue'));
const ReadAlso = defineAsyncComponent(() => import('~/components/BaseComponents/BaseReadAlso.vue'));
const TheRef = defineAsyncComponent(() => import('~/components/TheRef.vue'));
const TheRefAmp = defineAsyncComponent(() => import('~/components/Amp/TheRef.amp.vue'));
const GoodsItem = defineAsyncComponent(() => import('~/components/GoodsItem.vue'));
const GoodsItemAmp = defineAsyncComponent(() => import('~/components/Amp/GoodsItem.amp.vue'));
const GalleryProducts = defineAsyncComponent(() => import('~/components/GalleryProducts.vue'));
const BhAppbox = defineAsyncComponent(() => import('~/components/BhAppbox.vue'));
const Carousel = defineAsyncComponent(
  () => import('~/components/ArticlesCarousel/ArticlesCarouselWrapp.vue'),
);
const FAQ = defineAsyncComponent(() => import('~/components/FAQ.vue'));

const AdsPageContactsList = defineAsyncComponent(
  () => import('~/components/AdsPageContactsList.vue'),
);
const AmpAdsPageContactsList = defineAsyncComponent(
  () => import('~/components/Amp/AdsContactList.amp.vue'),
);
const AdsPageContactUsForm = defineAsyncComponent(
  () => import('~/components/AdsPageContactUsForm.vue'),
);
const AdsPageUsefulInfo = defineAsyncComponent(() => import('~/components/AdsPageUsefulInfo.vue'));
const AmpAdsPageUsefulInfo = defineAsyncComponent(
  () => import('~/components/Amp/AmpAdsPageUsefulInfo.vue'),
);

const AskQuestion = defineAsyncComponent(
  () => import('~/components/BaseComponents/BaseAskQuestion.vue'),
);

const AmpSpoiler = defineAsyncComponent(() => import('~/components/Amp/Spoiler.amp.vue'));
const AmpAskQuestion = defineAsyncComponent(() => import('~/components/Amp/AskQuestion.amp.vue'));
const AmpImageWrapper = defineAsyncComponent(
  () => import('~/components/Amp/AmpImageWrapper.amp.vue'),
);

const TiktokEmbed = defineAsyncComponent(() => import('~/components/TiktokEmbed.vue'));
const TwitterEmbed = defineAsyncComponent(() => import('~/components/TwitterEmbed.vue'));
const PinterestEmbed = defineAsyncComponent(() => import('~/components/PinterestEmbed.vue'));
const PinterestGroup = defineAsyncComponent(() => import('~/components/PinterestGroup.vue'));
const AppEmbed = defineAsyncComponent(() => import('~/components/AppEmbed.vue'));

const AmpTiktokEmbed = defineAsyncComponent(
  () => import('~/components/Amp/AmpTiktokEmbed.amp.vue'),
);
const AmpVimeoEmbed = defineAsyncComponent(() => import('~/components/Amp/AmpVimeoEmbed.amp.vue'));
const AmpYoutubeEmbed = defineAsyncComponent(
  () => import('~/components/Amp/AmpYoutubeEmbed.amp.vue'),
);
const BaseAmpIframe = defineAsyncComponent(() => import('~/components/Amp/BaseAmpIframe.amp.vue'));
const AmpTwitterEmbed = defineAsyncComponent(
  () => import('~/components/Amp/AmpTwitterEmbed.amp.vue'),
);
const AmpVkontakteIframeEmbed = defineAsyncComponent(
  () => import('~/components/Amp/VkIframe.amp.vue'),
);
const AmpVkontaktePostEmbed = defineAsyncComponent(
  () => import('~/components/Amp/VkPostEmbed.amp.vue'),
);
const AmpVkontaktePoll = defineAsyncComponent(
  () => import('~/components/Amp/AmpVkontaktePoll.amp.vue'),
);
const AmpPinterestEmbed = defineAsyncComponent(
  () => import('~/components/Amp/PinterestEmbed.amp.vue'),
);
const AmpPinterestGroupEmbed = defineAsyncComponent(
  () => import('~/components/Amp/PinterestGroupEmbed.amp.vue'),
);
const AmpIframeWrapper = defineAsyncComponent(
  () => import('~/components/Amp/AmpIframeWrapper.amp.vue'),
);
const AmpHtml = defineAsyncComponent(() => import('~/components/Amp/AmpHtml.amp.vue'));
const TimeAgo = defineAsyncComponent(() => import('~/components/Amp/TimeAgo.amp.vue'));
const AmpRandomHtml = defineAsyncComponent(() => import('~/components/Amp/AmpRandomHtml.amp.vue'));
const CarouselAmp = defineAsyncComponent(() => import('~/components/Amp/CarouselAmp.amp.vue'));
const AmpQuiz = defineAsyncComponent(() => import('~/components/Amp/AmpQuiz.amp.vue'));
const AppHtmlAmp = defineAsyncComponent(() => import('~/components/Amp/AppHtml.amp.vue'));
const AmpCoreTableOfContents = defineAsyncComponent(
  () => import('~/components/Amp/CoreTableOfContents.amp.vue'),
);
const AmpAdsPageContactUsForm = defineAsyncComponent(
  () => import('~/components/Amp/AmpAdsPageContactUsForm.amp.vue'),
);

const components: Record<string, any> = {
  burninghut__appbox: BhAppbox,
  'bh-kiss__buttons': CoreButtons,
  'bh-kiss__button': ArticleButton,
  'bh-kiss__reference': TheRef,
  'bh-kiss__paragraph': TheParagraph,
  'bh-kiss__heading': TheHeading,
  'bh-kiss__quote': Quote,
  'bh-kiss__author-quote': Quote,
  'bh-kiss__pullquote': Incut,
  'bh-kiss__table': TheTable,
  'bh-kiss__gallery': Gallery,
  'bh-kiss__image': ArticlePicture,
  'bh-kiss__image--mobile-screenshots': MobileScreenshots,
  'bh-kiss__separator': BaseSeparator,
  'bh-kiss__list': BHList,
  'bh-kiss__characters-list': CharactersList,

  'bh-kiss__group': BHGroup,
  'bh-kiss__group--mobile-screenshots': MobileScreenshots,
  'bh-kiss__group--pinterest-embed': PinterestGroup,

  'bh-kiss__html': AppHtml,

  'bh-kiss__link': BHLink,
  'bh-kiss__talker-comment-widget': CommentsWidget,
  'core__table-of-contents': CoreTableOfContents,
  core__column: CoreColumn,
  core__columns: CoreColumns,
  burninghut__infobox: Infobox,
  burninghut__greybox: GreyBox,
  'bh-kiss__posts-collection': PostsCollection,
  'bh-kiss__taxonomy-posts': TaxonomyPosts,
  'burninghut__comments-widget': CommentsWidget,
  'bh-kiss__top-posts': TopPosts,
  'pinterest-columns': PinterestColumns,
  'burninghut__subscriptions-block': SubscriptionsForm,
  'bh-kiss__promoblock': BasePromoblok,
  'bh-kiss__spoiler': Spoiler,
  'burninghut__read-also': ReadAlso,
  'bh-kiss__goods': GoodsItem,
  burninghut__quickpolls: QuickPolls,
  'bh-kiss__products-gallery': GalleryProducts,
  'bh-kiss__carousel': Carousel,
  'burninghut__ads-page-contacts-list': AdsPageContactsList,
  'burninghut__ads-page-contact-us-form': AdsPageContactUsForm,
  'bh-kiss__ads-page-useful-info': AdsPageUsefulInfo,
  'burninghut__ask-question': AskQuestion,
  bb__faq: FAQ,

  // эмбеды
  'bh-kiss__embed': AppEmbed,
  'bh-kiss__embed--tiktok': TiktokEmbed,
  'bh-kiss__embed--twitter': TwitterEmbed,
  'bh-kiss__embed--pinterest': PinterestEmbed,

  // AMP
  'amp-bh-kiss__ads-page-useful-info': AmpAdsPageUsefulInfo,
  'amp-article-picture': AmpArticlePicture,
  'amp-mobile-screenshots': AmpMobileScreenShots,
  'amp-gallery': AmpGallery,
  'amp-products-gallery': AmpGalleryProducts,
  'amp-reference': TheRefAmp,
  'amp-goods': GoodsItemAmp,
  'amp-spoiler': AmpSpoiler,
  'amp-ask-question': AmpAskQuestion,
  'amp-image-wrapper': AmpImageWrapper,
  'amp-tiktok-embed': AmpTiktokEmbed,
  'amp-youtube-embed': AmpYoutubeEmbed,
  'amp-vimeo-embed': AmpVimeoEmbed,
  'amp-twitter-embed': AmpTwitterEmbed,
  'base-amp-iframe': BaseAmpIframe,
  'amp-vkontakte-embed': AmpVkontakteIframeEmbed,
  'amp-vkontakte-post-embed': AmpVkontaktePostEmbed,
  'amp-vkontakte-poll': AmpVkontaktePoll,
  'amp-pinterest-embed': AmpPinterestEmbed,
  'amp-pinterest-group-embed': AmpPinterestGroupEmbed,
  'amp-iframe-wrapper': AmpIframeWrapper,
  'amp-html': AmpHtml,
  'time-ago': TimeAgo,
  'amp-random-html': AmpRandomHtml,
  'carousel-amp': CarouselAmp,
  'amp-quiz': AmpQuiz,
  'app-html-amp': AppHtmlAmp,
  'amp-core__table-of-contents': AmpCoreTableOfContents,
  'amp-burninghut__ads-page-contacts-list': AmpAdsPageContactsList,
  'amp-burninghut__ads-page-contact-us-form': AmpAdsPageContactUsForm,
};

export const excludeRenderComponents: string[] = [
  'bh-kiss__table',
  'bh-kiss__list',
  'burninghut__read-also',
  'bh-kiss__reference',
  'burninghut__infobox',
  'bh-kiss__products-gallery',
  'bh-kiss__gallery',
  'bh-kiss__goods',
  'burninghut__appbox',
  'bh-kiss__quote',
  'bh-kiss__author-quote',
  'burninghut__quickpolls',
  'core__table-of-contents',
  'amp-core__table-of-contents',

  'bh-kiss__image',
  'bh-kiss__image--mobile-screenshots',

  'bh-kiss__group--mobile-screenshots',
  'bh-kiss__group--pinterest-embed',

  'bh-kiss__html',

  'bh-kiss__taxonomy-posts',
  'bh-kiss__carousel',
  'burninghut__ads-page-contacts-list',
  'burninghut__ads-page-contact-us-form',
  'bh-kiss__ads-page-useful-info',
  'burninghut__ask-question',
  'bh-kiss__promoblock',

  'bh-kiss__embed',
  'bh-kiss__embed--tiktok',
  'bh-kiss__embed--twitter',
  'bh-kiss__embed--pinterest',
  'music-yandex-embed',

  'amp-article-picture',
  'amp-mobile-screenshots',
  'amp-gallery',
  'amp-products-gallery',
  'amp-reference',
  'amp-goods',
  'amp-spoiler',
  'amp-ask-question',
  'amp-burninghut__ads-page-contacts-list',
  'amp-tiktok-embed',
  'amp-youtube-embed',
  'amp-vimeo-embed',
  'amp-twitter-embed',
  'base-amp-iframe',
  'amp-vkontakte-embed',
  'amp-vkontakte-post-embed',
  'amp-vkontakte-poll',
  'amp-pinterest-embed',
  'amp-iframe-wrapper',
  'amp-html',
  'amp-random-html',
  'amp-bh-kiss__ads-page-useful-info',
  'amp-burninghut__ads-page-contact-us-form',

  'carousel-amp',
  'amp-quiz',
  'amp-pinterest-group-embed',
  'app-html-amp',
];

export const createVNode = (elem: TContent, h: typeof hFunction): VNode => {
  const { type, text, attributes, children } = elem;
  const textTypes = ['#text', '#comment', '#script-content'];
  const node = components[type] || type;

  let childNodes: VNode[] = [];

  if (textTypes.includes(type)) {
    return createTextVNode(text ?? '');
  }

  if (Array.isArray(children)) {
    childNodes = children.map((item) => createVNode(item, h));
  }

  if (excludeRenderComponents.includes(type)) {
    return h(node, { elem });
  } else {
    return h(
      node,
      {
        ...attributes,
        ...(type === 'a' ? { 'data-vars-link-href': attributes?.href } : {}),
        style: attributes?.style ?? null,
      },
      // Shows warning about content slots, check on single page
      { default: () => childNodes },
    );
  }
};
